import { API_BASE_URL } from '@/config'
import store from '@/store'
import type {
  CallApiParams,
  CallApiParamsFormData,
  CallApiParamsObject,
  CallApiReturn
} from './types'
import {
  NOTICE_ERROR_500_TITLE,
  NOTICE_ERROR_500_CONTENT,
  NOTICE_NETWORK_ERROR_TITLE,
  NOTICE_NETWORK_ERROR_CONTENT,
  NOTICE_DATA_ERROR_TITLE,
  NOTICE_DATA_ERROR_CONTENT
  // NOTICE_ERROR_403_TITLE,
  // NOTICE_ERROR_403_CONTENT
} from '@ui-notices/config'
import QueryString from 'query-string'

// пауза между повторными запросами
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const getRetryDelay = (
  attempt: number,
  strategy: NonNullable<CallApiParams['retryDelayStrategy']>,
  baseDelayMs: number,
  maxDelayMs: number,
  jitterRatio: number
) => {
  const delay =
    strategy === 'fixed' ? baseDelayMs : Math.min(baseDelayMs * 2 ** (attempt - 1), maxDelayMs)

  const jitter = delay * jitterRatio
  const delayWithJitter = delay - jitter + Math.random() * jitter * 2

  return Math.round(Math.min(delayWithJitter, maxDelayMs))
}

const _buildRequestUrl = (url: string, params?: object, remote = false) => {
  let requestUrl = remote ? url : `${API_BASE_URL}${url}`
  if (params) {
    const strParams = QueryString.stringify(params, {
      skipNull: true
    })
    requestUrl = requestUrl + '?' + strParams
  }
  return requestUrl
}

const _buildRequestBody = (data: object | FormData): FormData => {
  if (data instanceof FormData) return data

  const formData = new FormData()

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      formData.append(key, data[key as keyof object])
    }
  }

  return formData
}

async function callApi<T, E = string[]>(params: CallApiParamsObject): Promise<CallApiReturn<T, E>>
async function callApi<T, E = string[]>(params: CallApiParamsFormData): Promise<CallApiReturn<T, E>>
async function callApi<T, E = string[]>({
  method,
  url,
  data,
  params,
  remote = false,
  authRequired = true,
  formData = false,
  headers = undefined,
  denyLogout = false,
  denyNotice = false,
  readTextErrors = false,
  retries = 5, // <- дефолтное количество попыток
  retryDelayStrategy = 'exponential',
  retryDelayMs = 2000,
  retryDelayMaxMs = 30000,
  retryDelayJitterRatio = 0.2
}: CallApiParams): Promise<CallApiReturn<T, E>> {
  const authToken = store.user.auth.getToken()

  const response: CallApiReturn<T, E> = {
    data: null,
    errors: null,
    status: 0
  }
  const requestConfig: RequestInit = {
    headers: {
      Accept: 'application/json, text/plain, */*'
    },
    method: method.toUpperCase()
  }

  const requestUrl = _buildRequestUrl(url, params, remote)

  // request headers configuration
  if (authRequired && authToken)
    requestConfig.headers = { ...requestConfig.headers, Authorization: `Token ${authToken}` }

  if (headers) {
    requestConfig.headers = { ...requestConfig.headers, ...headers }
  }

  // request body building
  if (data) {
    if (formData) requestConfig.body = _buildRequestBody(data)
    else {
      requestConfig.headers = { ...requestConfig.headers, 'Content-Type': `application/json` }
      requestConfig.body = JSON.stringify(data)
    }
  }

  // цикл авто повторов (retries)
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const fetchResponse = await fetch(requestUrl, requestConfig)
      response.status = fetchResponse.status

      // success handling
      if (fetchResponse.ok) {
        response.data = await fetchResponse.json()
      } else {
        if (fetchResponse.headers.get('Content-Type')?.match('application/json')) {
          response.errors = await fetchResponse.json()
        } else if (readTextErrors && response.status >= 400 && response.status < 500) {
          const errorText = (await fetchResponse.text()).trim()
          response.errors = (errorText || 'Не удалось отправить ответ') as unknown as E
        }
      }

      // error handling
      if (response.status === 401 && !denyLogout) store.user.auth.logout()
      if (response.status === 403 && !denyNotice) {
        // Логика для 403
      }
      if (response.status === 404 && !denyNotice) {
        // Логика для 404
      }
      if (response.status >= 500 && !denyNotice) {
        // ошибки сервера (500+) не переотправляем, это к бэку
        if (attempt === retries) {
          store.ui.notices.newError(NOTICE_ERROR_500_TITLE, NOTICE_ERROR_500_CONTENT)
        }
        response.errors = null
      }

      // если запрос ок (или вернул ошибку типа 4хх / 5xx), прерываем цикл и возвращаем результат
      return response
    } catch (exception) {
      console.error(
        `[API Wrapper Attempt ${attempt}/${retries} Failed] ${method} ${requestUrl}:`,
        exception
      )

      if (exception instanceof Error) {
        const isNetworkError =
          exception.message.includes('NetworkError') ||
          exception.message.includes('Failed to fetch') ||
          exception.message.includes('Load failed') // Safari

        // если сетевая ошибка (таймаут, пропал коннект) и еще есть попытки в запасе
        if (isNetworkError && attempt < retries) {
          await sleep(
            getRetryDelay(
              attempt,
              retryDelayStrategy,
              retryDelayMs,
              retryDelayMaxMs,
              retryDelayJitterRatio
            )
          )
          continue
        }

        // если полали сюда, либо это последняя попытка, либо ошибка не сетевая (напр. SyntaxError)
        if (attempt === retries && !denyNotice) {
          if (isNetworkError) {
            store.ui.notices.newError(NOTICE_NETWORK_ERROR_TITLE, NOTICE_NETWORK_ERROR_CONTENT)
          } else if (exception.name === 'SyntaxError') {
            store.ui.notices.newError(NOTICE_DATA_ERROR_TITLE, NOTICE_DATA_ERROR_CONTENT)
          } else {
            store.ui.notices.newError(NOTICE_ERROR_500_TITLE, NOTICE_ERROR_500_CONTENT)
          }
        }
      } else {
        // непредвиденный тип ошибки
        if (attempt === retries && !denyNotice) {
          store.ui.notices.newError(NOTICE_ERROR_500_TITLE, NOTICE_ERROR_500_CONTENT)
        }
      }
    }
  }

  return response
}

export { callApi }
